import { useCallback, useMemo } from "react";
import { useAppSelector } from "@/store/hooks";
import {
  getEstablishmentIdsForGenerators,
  useFilterOptions,
} from "@/hooks/useFilterOptions";
import { useDemoMode } from "@/contexts/DemoModeContext";
import { maskEstablishmentName, maskGeneratorName } from "@/utils/demoMode";
import {
  buildFilterChips,
  type DashboardFilters,
  type FilterChip,
} from "@/hooks/useDashboardFilters";
import type { FilterOption } from "@/components/shared/filters/OptionsFilterDropdown";

interface UseDashboardFilterOptionsResult {
  isLoading: boolean;
  materialOptions: FilterOption[];
  stateOptions: FilterOption[];
  establishmentOptions: FilterOption[];
  generatorOptions: FilterOption[];
  /** Mesma regra da barra antiga: só quem acessa mais de um gerador filtra por gerador. */
  showGeneratorFilter: boolean;
  chips: FilterChip[];
  /** Ao trocar de gerador, seleciona automaticamente seus estabelecimentos. */
  withGenerators: (next: string[]) => Partial<DashboardFilters>;
}

/**
 * Opções, rótulos e cascata dos filtros de dashboard. Concentra o acesso ao
 * Redux e ao modo demonstração, deixando a barra apenas com a apresentação.
 */
export const useDashboardFilterOptions = (
  filters: DashboardFilters,
): UseDashboardFilterOptionsResult => {
  const isLoading = useAppSelector(
    (state) => state.establishmentAccess.loading,
  );
  const rawEstablishments = useAppSelector(
    (state) => state.establishmentAccess.establishments,
  );
  const rawGenerators = useAppSelector(
    (state) => state.establishmentAccess.generators,
  );
  const { isDemoMode } = useDemoMode();
  const { establishments, generators, states, materialOptions } =
    useFilterOptions({ selectedGeneratorExternalIds: filters.generators });

  // Dashboard usa o id numérico (não o external_id) para os filtros irem
  // legíveis na URL — `?geradores=5&unidades=42` em vez de dois UUIDs.
  const establishmentOptions = useMemo(
    () =>
      establishments.map((item) => ({
        value: item.establishment_numeric_id,
        label: maskEstablishmentName(item.establishment_name, isDemoMode),
      })),
    [establishments, isDemoMode],
  );

  const generatorOptions = useMemo(
    () =>
      generators.map((item) => ({
        value: item.generator_numeric_id,
        label: maskGeneratorName(item.generator_name, isDemoMode),
      })),
    [generators, isDemoMode],
  );

  const chips = useMemo(() => {
    const toMap = (options: FilterOption[]) =>
      new Map(options.map((option) => [option.value, option.label]));

    return buildFilterChips(filters, {
      materials: toMap(materialOptions),
      states: toMap(states),
      establishments: toMap(establishmentOptions),
      generators: toMap(generatorOptions),
    });
  }, [filters, materialOptions, states, establishmentOptions, generatorOptions]);

  const withGenerators = useCallback(
    (next: string[]): Partial<DashboardFilters> => {
      if (next.length === 0) {
        // Removeu o(s) último(s) gerador(es): limpa os estabelecimentos que
        // a cascata tinha selecionado junto.
        return { generators: next, establishments: [] };
      }

      // Gerador(es) selecionado(s): seleciona automaticamente todos os
      // estabelecimentos filhos, igual à cascata das outras telas de filtro.
      const allowedIds = getEstablishmentIdsForGenerators(
        rawEstablishments,
        rawGenerators,
        next,
      );
      const establishments = allowedIds
        ? rawEstablishments.filter((e) => allowedIds.has(e.id)).map((e) => e.id)
        : [];
      return { generators: next, establishments };
    },
    [rawEstablishments, rawGenerators],
  );

  return {
    isLoading,
    materialOptions,
    stateOptions: states,
    establishmentOptions,
    generatorOptions,
    showGeneratorFilter: generators.length > 1,
    chips,
    withGenerators,
  };
};
